import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT automatically
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('mq_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Global 401 -> logout
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('mq_token');
      localStorage.removeItem('mq_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// -- AUTH -----------------------------------------------------
export const authAPI = {
  login:  (email, password) => api.post('/auth/login', { email, password }),
  me:     ()                => api.get('/auth/me'),
};

// -- CUSTOMERS ------------------------------------------------
export const customersAPI = {
  list:         (params)   => api.get('/customers', { params }),
  get:          (id)       => api.get(`/customers/${id}`),
  create:       (data)     => api.post('/customers', data),
  update:       (id, data) => api.put(`/customers/${id}`, data),
  getQuotes:    (id)       => api.get(`/customers/${id}/quotes`),
  assignVendor: (id, user_id, account_origin, notes) => api.post(`/customers/${id}/assign`, { user_id, account_origin, notes }),
  assignments:  ()         => api.get('/assignments'),
  compras:      (customer_ids) => api.post('/assignments/compras', { customer_ids }),
};

// -- QUOTES ---------------------------------------------------
export const quotesAPI = {
  list:         (params)        => api.get('/quotes', { params }),
  get:          (id)            => api.get(`/quotes/${id}`),
  update:       (id, data)      => api.put(`/quotes/${id}`, data),
  costSummary:  (id)            => api.get(`/quotes/${id}/cost-summary`),
  create:       (data)          => api.post('/quotes', data),
  updateStatus: (id, data)      => api.patch(`/quotes/${id}/status`, data),
  duplicate:    (id)            => api.post(`/quotes/${id}/duplicate`),
  pdfUrl:       (id)            => `${api.defaults.baseURL}/quotes/${id}/pdf`,
  pushToOdoo:   (id)            => api.post(`/quotes/${id}/odoo/push`),

  addAluLine:    (id, data)     => api.post(`/quotes/${id}/lines/alu`, data),
  updateAluLine: (id, lid, data)=> api.put(`/quotes/${id}/lines/alu/${lid}`, data),
  deleteAluLine: (id, lid)      => api.delete(`/quotes/${id}/lines/alu/${lid}`),

  addIronLine:   (id, data)     => api.post(`/quotes/${id}/lines/iron`, data),
  updateIronLine: (id, lineId, data) => api.put(`/quotes/${id}/lines/iron/${lineId}`, data),
  deleteIronLine:(id, lid)      => api.delete(`/quotes/${id}/lines/iron/${lid}`),
  // migr 076: aprobar/desaprobar labor por linea (manager/admin).
  approveIronLabor: (id, lineId, approved) =>
    api.post(`/quotes/${id}/lines/iron/${lineId}/approve-labor`, { approved }),
};

// -- PRICING ENGINE -------------------------------------------
export const pricingAPI = {
  calcAlu:    (data) => api.post('/pricing/alu/calculate', data),
  calcIron:   (data) => api.post('/pricing/iron/calculate', data),
  glassLookup:(data) => api.post('/pricing/glass/lookup', data),
  calcBuiria: (data) => api.post('/pricing/buiria/calculate', data),
};

// -- CATALOG --------------------------------------------------
export const catalogAPI = {
  aluModels:       () => api.get('/catalog/alu-models'),
  aluTypeSeries:   () => api.get('/catalog/alu-type-series'),
  aluConstraints:  () => api.get('/catalog/alu-constraints'),
  aluCatalog:      () => api.get('/catalog/alu-catalog'),
  ironCategories:  () => api.get('/catalog/iron-categories'),
  ironDesigns:     (category_id) => api.get('/catalog/iron-designs', { params: { category_id } }),
  ironGlass:       () => api.get('/catalog/iron-glass'),
  ironColors:      () => api.get('/catalog/iron-colors'),
  ironHandles:     () => api.get('/catalog/iron-handles'),
  ironLiteDefaults:() => api.get('/catalog/iron-lite-defaults'),
  ironKits:        () => api.get('/catalog/iron-kits'),
  ironLaborScales: () => api.get('/catalog/iron-labor-scales'),
  buiriaRates:     () => api.get('/catalog/buiria-rates'),
  glassTypes:      () => api.get('/catalog/glass-types'),
  glassThicknesses:() => api.get('/catalog/glass-thicknesses'),
  glassVendors:    () => api.get('/catalog/glass-vendors'),
};

// -- ADMIN ----------------------------------------------------
export const adminAPI = {
  getPriceParams:       ()       => api.get('/admin/price-params'),
  updatePriceParams:    (data)   => api.post('/admin/price-params', data),
  updateAluModel:       (id, d)  => api.put(`/admin/catalog/alu-models/${id}`, d),
  getGlassPricesFlat:   ()       => api.get('/admin/glass/prices/flat'),
  createGlassPriceFlat: (d)      => api.post('/admin/glass/prices/flat', d),
  getGlassPricesCurved: ()       => api.get('/admin/glass/prices/curved'),
  createGlassPriceCurved:(d)     => api.post('/admin/glass/prices/curved', d),
};

// -- USERS ----------------------------------------------------
// -- COMISIONES -----------------------------------------------
// Consulta Odoo cobro por cobro: una quincena tarda minutos, por eso el
// timeout largo. Misma fuente que el Excel de Rosamalia.
export const commissionsAPI = {
  report: (desde, hasta) => api.get('/commissions/report', {
    params: { desde, hasta }, timeout: 900000,
  }),
};

// -- CORTES DE COMISION ---------------------------------------
export const commissionRunsAPI = {
  listar: ()   => api.get('/commissions/runs'),
  ver:    (id) => api.get(`/commissions/runs/${id}`),
  // Nueve minutos de limite: el calculo consulta Odoo factura por factura.
  crear:  (desde, hasta) => api.post('/commissions/runs', { desde, hasta }, { timeout: 900000 }),
  cerrar: (id, notas)    => api.post(`/commissions/runs/${id}/cerrar`, { notas }),
  // Busca un renglon por factura, SO o cliente en TODOS los cortes. De aqui
  // sale el autollenado del formulario de ajustes, incluidas las fechas del
  // periodo que se esta corrigiendo.
  buscarRenglon: (q)        => api.get('/commissions/lines/buscar', { params: { q } }),
  agregarAjuste: (id, data) => api.post(`/commissions/runs/${id}/ajustes`, data),
  editarAjuste:  (id, aid, data) => api.put(`/commissions/runs/${id}/ajustes/${aid}`, data),
  borrarAjuste:  (id, aid)  => api.delete(`/commissions/runs/${id}/ajustes/${aid}`),
};

export const usersAPI = {
  list:   ()        => api.get('/users'),
  salespeople: ()  => api.get('/salespeople'),
  create: (data)    => api.post('/users', data),
  update: (id, data)=> api.put(`/users/${id}`, data),
  changePassword: (id, data) => api.patch(`/users/${id}/password`, data),
};

// -- DASHBOARD ------------------------------------------------
export const dashboardAPI = {
  // 'vend' es el filtro por vendedor; sin el, Total AMS. summary, win-rate y
  // by-salesperson respetan el periodo; pipeline y alerts no lo reciben a
  // proposito, son estado de hoy.
  summary:       (from, to, vend) => api.get('/dashboard/summary',        { params: { from, to, vend } }),
  pipeline:      (vend)           => api.get('/dashboard/pipeline',       { params: { vend } }),
  winRate:       (from, to, vend) => api.get('/dashboard/win-rate',       { params: { from, to, vend } }),
  bySalesperson: (from, to)       => api.get('/dashboard/by-salesperson', { params: { from, to } }),
  alerts:        (vend)           => api.get('/dashboard/alerts',         { params: { vend } }),
};

export default api;

// -- DASHBOARD EJECUTIVO --------------------------------------
export const executiveAPI = {
  revenueMonthly:   (from, to) => api.get('/executive/revenue/monthly', { params: { from, to } }),
  revenueDrilldown: (domain, limit = 100, offset = 0) =>
    api.post('/executive/revenue/drilldown', { domain, limit, offset }),
  revenueByFamily:  (from, to) => api.get('/executive/revenue/by-family', { params: { from, to } }),
  // Cartera. El backend es carteraController y se publico el 25-oct-2026.
  // NO recibe rango de fechas a proposito: la cartera es siempre al dia de
  // hoy, porque Odoo calcula el saldo pendiente al momento de la consulta.
  cartera:           () => api.get('/executive/cartera'),
  carteraDrilldown:  (body) => api.post('/executive/cartera/drilldown', body),
  carteraDocumento:  (move_id) => api.post('/executive/cartera/document', { move_id }),
  carteraRefresh:    () => api.post('/executive/cartera/refresh'),
  refresh:          () => api.post('/executive/refresh'),
};
